import { createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { Auth } from "aws-amplify";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { IActionMeta } from "src/app-redux/store";
import { ax } from "src/common/api/apiClient";
import { IApi } from "src/common/typings/app";
import { IUser } from "src/common/typings/user";
import { userAttribs } from "src/common/utils/userAttribs4mLocalStorage";
import { AuthState } from "../authSlice";

export const handleLogin = (
  state: AuthState,
  action: PayloadAction<IUser, string, IActionMeta<ILoginArgs | void>>
) => {
  state.status = "idle";
  state.user = action.payload;
  delete state.authError;
};

export const loginFailed = (
  state: AuthState,
  action: PayloadAction<
    unknown,
    string,
    IActionMeta<ILoginArgs>,
    { code?: string; message?: string; name?: string }
  >
) => {
  if (action.error.code === "PasswordResetRequiredException") {
    state.status = "passwordResetRequired";
  } else state.status = "failed";
  delete state.user;
  state.authError = action?.error?.message || "An Unexpected Error Occurred";
};

type ILoginArgs = {
  email: string;
  password: string;
  newPassword: string;
};
export const loginAction = createAsyncThunk(
  "auth/loginAction",
  async ({ email, password, newPassword }: ILoginArgs): Promise<IUser> => {
    try {
      const cogUser = await Auth.signIn(email, password);
      //In case if the user is login into the system for the first time.
      if (cogUser.challengeName === "NEW_PASSWORD_REQUIRED" && newPassword !== "") {
        await Auth.completeNewPassword(cogUser, newPassword);
        return JSON.parse("{}") as IUser;
      }
      const attribs = userAttribs();
      if (attribs) {
        const res = await chackValidateUserAction();
        if (!res) {
          throw new Error("Unable to extract cognito user info from localStorage");
        } else {
          return JSON.parse(attribs) as IUser;
        }
      } else throw new Error("Unable to extract cognito user info from localStorage");
    } catch (error: any) {
      if (error.code === "PasswordResetRequiredException")
        throw { code: error.code, message: "Password Reset Required" };
      throw error.message || error;
    }
  }
);

export const currentUserAction = createAsyncThunk("auth/currentUserAction", async () => {
  try {
    await Auth.currentAuthenticatedUser();
    const attribs = userAttribs();
    if (attribs) {
      return JSON.parse(attribs) as IUser;
    } else throw new Error("Unable to extract cognito user info from localStorage");
  } catch (error) {
    throw error;
  }
});

export const chackValidateUserAction = async () => {
  try {
    const res = await ax.get<IApi>("/validate-vendor-portal-user", {
      headers: {
        "Content-Type": "application/json",
      },
    });
    if (res.status === 200) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    return false;
  }
};
export const logOutAction = createAsyncThunk("auth/logOutAction", async () => {
  return Auth.signOut();
});

type ISetPassArgs = {
  token: string;
  password: string;
};
export const setPasswordAction = createAsyncThunk(
  "management/setPassword",
  async (user: ISetPassArgs, { dispatch }) => {
    const parentUrl = process.env.REACT_APP_API_URL;
    try {
      const res: any = await fetch(`${parentUrl}user-set-password`, {
        method: "POST",
        body: JSON.stringify(user),
        headers: {
          "Content-type": "application/json; charset=UTF-8",
        },
      });
      const resStatus: any = await res.json();
      if (res.status === 200 && resStatus.success) {
        dispatch(
          addNotificationAction({
            message: "Password has been changed successfully!",
            options: { variant: "success" },
          })
        );
        return user;
      } else throw new Error(resStatus.error || res.statusText);
    } catch (error) {
      throw error;
    }
  }
);
